<template>
  <el-container class="background-white amazon-returns-sync-page">
    <el-header class="header-container">
      <h1 class="header-title">Amazon Returns Sync Monitor <small>(FBM)</small></h1>
    </el-header>

    <el-main class="main-content">
      <el-row :gutter="12" class="controls-row" align="middle">
        <el-col :xs="24" :sm="8" :md="7" class="control-col">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="mini"
            value-format="yyyy-MM-dd"
            unlink-panels
            range-separator="to"
            start-placeholder="From"
            end-placeholder="To"
            @change="onDateRangeChange"
            class="control-input"
            style="width: 100%"
          />
        </el-col>

        <el-col :xs="24" :sm="8" :md="4" class="control-col">
          <el-select v-model="filters.status" size="mini" class="control-input" style="width: 100%" @change="refreshAll">
            <el-option label="All" value="all" />
            <el-option label="Mismatch" value="mismatch" />
            <el-option label="Matched" value="matched" />
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="8" :md="7" class="control-col">
          <el-input
            v-model="filters.search"
            size="mini"
            clearable
            placeholder="Search RMA / Order / RA"
            @keyup.enter.native="refreshAll"
            @clear="refreshAll"
            class="control-input"
          >
            <el-button slot="append" icon="el-icon-search" @click="refreshAll" />
          </el-input>
        </el-col>

        <el-col :xs="24" :sm="16" :md="6" class="text-right">
          <el-button size="mini" type="primary" :loading="loadingSummary" @click="refreshAll">Refresh</el-button>
          <el-button size="mini" :disabled="loadingDetails || !detailRows.length" @click="exportCsv">Export CSV</el-button>
        </el-col>
      </el-row>

      <el-row :gutter="12" class="live-push-row" align="middle">
        <el-col :span="24" class="live-push-col">
          <el-switch
            v-model="settings.live_push_enabled"
            :disabled="savingSettings"
            active-text="Live D365 push"
            @change="onLivePushToggle"
          />
          <el-tooltip
            placement="top"
            content="ON: Amazon returns are also pushed to the real D365 DTC_ReturnOrders table. OFF: returns are saved to the Acadia staging table only."
          >
            <i class="el-icon-info live-push-info" />
          </el-tooltip>
        </el-col>
      </el-row>

      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="source-banner"
        :title="`D365 source: ${meta.downstream_source || 'acadia_dtc_return_orders'} | Last sync: ${displayDate(lastSyncAt)}`"
      />

      <el-row :gutter="12" class="kpi-row">
        <el-col :xs="24" :sm="8">
          <div class="kpi-card"><div class="kpi-label">Acadia</div><div class="kpi-value">{{ totals.acadia }}</div></div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="kpi-card"><div class="kpi-label">D365 (Test)</div><div class="kpi-value">{{ totals.d365_test }}</div></div>
        </el-col>
        <el-col :xs="24" :sm="8">
          <div class="kpi-card mismatch"><div class="kpi-label">Open Mismatch</div><div class="kpi-value">{{ totals.open_mismatches }}</div></div>
        </el-col>
      </el-row>

      <el-card shadow="never" class="section-card">
        <div slot="header" class="section-title">Daily Health Summary</div>
        <el-table
          v-loading="loadingSummary"
          :data="summaryRows"
          stripe
          border
          :fit="true"
          :row-style="pointerRowStyle"
          max-height="500"
          @row-click="onSummaryRowClick"
          :row-class-name="summaryRowClassName"
        >
          <el-table-column prop="business_date" label="Date (Return Request Date)" min-width="210" />
          <el-table-column prop="acadia_return_count" label="Acadia" min-width="120" />
          <el-table-column prop="d365_ra_count" label="D365" min-width="130" />
          <el-table-column prop="variance_acadia_d365" label="Acadia-D365" min-width="110" />
          <el-table-column prop="mismatch_count" label="Mismatch" min-width="120" />
          <el-table-column label="Status" min-width="140">
            <template #default="{ row }">
              <el-tag size="mini" :type="statusTagType(row.status)">{{ row.status }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card shadow="never" class="section-card">
        <div slot="header" class="section-title">
          Return Details
          <small v-if="selectedDate">(Selected date: {{ selectedDate }})</small>
        </div>

        <el-table v-loading="loadingDetails" :data="detailRows" stripe border :row-style="pointerRowStyle">
          <el-table-column prop="amazon_return_id" label="Return ID" min-width="170" />
          <el-table-column prop="amazon_order_id" label="Order ID" min-width="170" />
          <el-table-column label="Acadia Created (PT)" min-width="165">
            <template #default="{ row }">{{ displayDate(row.acadia_created_at) }}</template>
          </el-table-column>
          <el-table-column prop="d365_staging_ra_id" label="D365 Staging RA ID" min-width="120" />
          <el-table-column prop="d365_ra_id" label="D365 RA ID" min-width="120" />
          <el-table-column prop="d365_sales_order" label="SalesOrder" min-width="130" />
          <el-table-column label="D365 Created (PT)" min-width="165">
            <template #default="{ row }">{{ displayDate(row.d365_created_at) }}</template>
          </el-table-column>
          <el-table-column label="Presence" min-width="140">
            <template #default="{ row }">
              <span>{{ row.system_presence }}</span>
              <el-tooltip
                v-if="row.in_d365_live"
                placement="top"
                :content="`In D365 DTC_ReturnOrders: ${row.d365_live_row_ids || ''}`"
              >
                <i class="el-icon-star-on live-presence-star" />
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="Status" width="120">
            <template #default="{ row }">
              <el-tag size="mini" :type="statusTagType(row.status)">{{ row.status }}</el-tag>
            </template>
          </el-table-column>

        </el-table>

        <div class="pagination-row">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :total="detailPagination.total"
            :page-size="detailPagination.per_page"
            :current-page="detailPagination.page"
            :page-sizes="[50, 100, 200]"
            @size-change="onPerPageChange"
            @current-change="onPageChange"
          />
        </div>
      </el-card>
    </el-main>
  </el-container>
</template>

<script>
import axios, {RepositoryError} from '../../../repositories';

export default {
  name: 'AmazonReturnsSyncPage',
  data() {
    const to = this.formatYmd(new Date());
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 6);
    const from = this.formatYmd(fromDate);

    return {
      filters: {
        from_date: from,
        to_date: to,
        status: 'all',
        search: '',
        timezone: 'America/Vancouver',
      },
      dateRange: [from, to],
      loadingSummary: false,
      loadingDetails: false,
      summaryRows: [],
      detailRows: [],
      appliedDetailParams: null,
      selectedDate: null,
      detailPagination: {
        page: 1,
        per_page: 50,
        total: 0,
      },
      totals: {
        acadia: 0,
        d365_test: 0,
        open_mismatches: 0,
      },
      meta: {},
      lastSyncAt: null,
      settings: {
        live_push_enabled: false,
      },
      savingSettings: false,
    };
  },
  created() {
    this.hydrateFromQuery();
    this.fetchSettings();
    this.refreshAll();
  },
  methods: {
    formatYmd(dateLike) {
      if (typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) {
        return dateLike;
      }
      const d = new Date(dateLike);
      if (Number.isNaN(d.getTime())) {
        return '';
      }
      const y = d.getFullYear();
      const m = `${d.getMonth() + 1}`.padStart(2, '0');
      const day = `${d.getDate()}`.padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    hydrateFromQuery() {
      const q = this.$route.query || {};
      const allowedStatus = ['all', 'matched', 'mismatch'];
      if (q.from_date && q.to_date) {
        this.filters.from_date = q.from_date;
        this.filters.to_date = q.to_date;
        this.dateRange = [q.from_date, q.to_date];
      }
      if (q.status && allowedStatus.includes(q.status)) this.filters.status = q.status;
      if (q.search) this.filters.search = q.search;
      if (q.selected_date) this.selectedDate = q.selected_date;
      if (q.page) this.detailPagination.page = Number(q.page) || 1;
      if (q.per_page) this.detailPagination.per_page = Number(q.per_page) || 50;
    },
    pushQuery() {
      const query = {
        from_date: this.filters.from_date,
        to_date: this.filters.to_date,
        status: this.filters.status,
        search: this.filters.search || undefined,
        selected_date: this.selectedDate || undefined,
        page: String(this.detailPagination.page),
        per_page: String(this.detailPagination.per_page),
      };
      this.$router.push({ query }).catch(() => {});
    },
    onDateRangeChange(value) {
      if (!value || value.length !== 2) {
        return;
      }
      const from = this.formatYmd(value[0]);
      const to = this.formatYmd(value[1]);
      if (!from || !to) {
        this.$message.error('Invalid date range');
        return;
      }
      this.filters.from_date = from;
      this.filters.to_date = to;
      this.refreshAll();
    },
    async refreshAll() {
      this.detailPagination.page = 1;
      await this.fetchSummary();
      await this.fetchDetails();
      this.pushQuery();
    },
    async fetchSettings() {
      try {
        const response = await axios.get('/reports/amazon-returns-sync/settings');
        this.settings.live_push_enabled = !!response.data?.live_push_enabled;
      } catch (err) {
        this.$message.warning('Could not load the live D365 push setting');
      }
    },
    async onLivePushToggle(enabled) {
      if (enabled) {
        try {
          await this.$confirm(
            'Returns will be written to the production D365 DTC_ReturnOrders table in addition to the Acadia staging table. Continue?',
            'Enable live D365 push',
            { confirmButtonText: 'Enable', cancelButtonText: 'Cancel', type: 'warning' },
          );
        } catch (cancelled) {
          this.settings.live_push_enabled = false;
          return;
        }
      }

      this.savingSettings = true;
      try {
        const response = await axios.put('/reports/amazon-returns-sync/settings', { live_push_enabled: enabled });
        this.settings.live_push_enabled = !!response.data?.live_push_enabled;
        this.$message.success(enabled ? 'Live D365 push enabled' : 'Live D365 push disabled');
        await this.fetchSummary();
      } catch (err) {
        this.settings.live_push_enabled = !enabled;
        // The axios interceptor rethrows plain Errors (no .response), so the server's
        // text is only ever in err.message.
        this.$message.error(err?.message || 'Failed to update the live D365 push setting');
      } finally {
        this.savingSettings = false;
      }
    },
    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const response = await axios.get('/reports/amazon-returns-sync/summary', { params: this.filters });
        this.summaryRows = response.data?.data || [];
        this.totals = response.data?.totals || this.totals;
        this.meta = response.data?.meta || {};
        this.lastSyncAt = this.summaryRows[0]?.last_sync_at || null;

        if (!this.selectedDate) {
          this.selectedDate = this.summaryRows[0]?.business_date || null;
        } else {
          const found = this.summaryRows.find((row) => row.business_date === this.selectedDate);
          if (!found) {
            this.selectedDate = this.summaryRows[0]?.business_date || null;
          }
        }
      } catch (err) {
        if (err instanceof RepositoryError) {
          this.$message.error(err.message || 'Failed to load summary');
        } else {
          this.$message.error(err?.response?.data?.message || 'Failed to load summary');
        }
      } finally {
        this.loadingSummary = false;
      }
    },
    async fetchDetails() {
      if (!this.selectedDate) {
        this.detailRows = [];
        this.appliedDetailParams = null;
        this.detailPagination.total = 0;
        return;
      }

      this.loadingDetails = true;
      try {
        const params = {
          business_date: this.selectedDate,
          status: this.filters.status,
          search: this.filters.search || undefined,
          timezone: this.filters.timezone,
          page: this.detailPagination.page,
          per_page: this.detailPagination.per_page,
        };

        const response = await axios.get('/reports/amazon-returns-sync/details', { params });
        this.detailRows = response.data?.data || [];
        this.appliedDetailParams = {...params};
        const meta = response.data?.meta || {};
        this.detailPagination.total = Number(meta.total || 0);
      } catch (err) {
        if (err instanceof RepositoryError) {
          this.$message.error(err.message || 'Failed to load details');
        } else {
          this.$message.error(err?.response?.data?.message || 'Failed to load details');
        }
      } finally {
        this.loadingDetails = false;
      }
    },
    async onSummaryRowClick(row) {
      this.selectedDate = row.business_date;
      this.detailPagination.page = 1;
      await this.fetchDetails();
      this.pushQuery();
    },
    async onPageChange(page) {
      this.detailPagination.page = page;
      await this.fetchDetails();
      this.pushQuery();
    },
    async onPerPageChange(perPage) {
      this.detailPagination.per_page = perPage;
      this.detailPagination.page = 1;
      await this.fetchDetails();
      this.pushQuery();
    },
    statusTagType(status) {
      if (status === 'Mismatch') return 'danger';
      return 'success';
    },
    summaryRowClassName({ row }) {
      return row.status === 'Mismatch' ? 'summary-row-mismatch' : '';
    },
    pointerRowStyle() {
      return { cursor: 'pointer' };
    },
    displayDate(value) {
      if (!value) return '-';
      return value.slice(0, 16).replace('T', ' ');
    },

    async exportCsv() {
      if (!this.detailRows.length || !this.appliedDetailParams) {
        this.$message.warning('No detail rows to export');
        return;
      }

      try {
        const params = {...this.appliedDetailParams};
        const response = await axios.get('/reports/amazon-returns-sync/export', { params, responseType: 'blob' });
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `amazon_returns_sync_${params.business_date || 'export'}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.$message.error(err?.response?.data?.message || 'Failed to export CSV');
      }
    },
  },
};
</script>

<style scoped>
.header-container {
  display: flex;
  align-items: center;
  padding: 15px 25px;
  border-bottom: 1px solid #e8e8e8;
  background: #f8f9fa;
}

.header-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.main-content {
  padding: 18px;
}

.controls-row {
  margin-bottom: 12px;
}

.control-col {
  margin-bottom: 8px;
}

.source-banner {
  margin-bottom: 12px;
}

.live-push-row {
  margin-bottom: 12px;
}

.live-push-col {
  display: flex;
  align-items: center;
  gap: 8px;
}

.live-push-info {
  color: #909399;
  cursor: help;
}

.live-presence-star {
  color: #e6a23c;
  margin-left: 4px;
  font-size: 16px;
  vertical-align: middle;
}

.kpi-row {
  margin-bottom: 12px;
}

.kpi-card {
  background: linear-gradient(120deg, #f4f7fb, #eef4ff);
  border: 1px solid #dbe7ff;
  border-radius: 8px;
  padding: 12px;
}

.kpi-card.mismatch {
  background: linear-gradient(120deg, #fff7f7, #ffe8e8);
  border-color: #ffd2d2;
}

.kpi-label {
  font-size: 12px;
  color: #5f6c7b;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: #243447;
}

.section-card {
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
}

::v-deep(.el-table) {
  border: 1px solid #d6deea !important;
}

::v-deep(.el-table--border::after),
::v-deep(.el-table::before) {
  background-color: #d6deea !important;
}

::v-deep(.el-table th.is-leaf),
::v-deep(.el-table td) {
  border-bottom: 1px solid #d6deea !important;
}

::v-deep(.el-table--border th),
::v-deep(.el-table--border td) {
  border-right: 1px solid #d6deea !important;
}

.pagination-row {
  margin-top: 12px;
  text-align: right;
}

.text-right {
  text-align: right;
}

::v-deep(.summary-row-mismatch) {
  background: #fff4f4;
}

::v-deep(.section-card .el-table__body .el-table__row) {
  cursor: pointer;
}

@media screen and (max-width: 768px) {
  .text-right {
    text-align: left;
    margin-top: 8px;
  }

  .header-title {
    font-size: 20px;
  }
}
</style>
